import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, AlertTriangle, Settings, CheckCircle, XCircle, Loader2, Zap, Users, Calendar, CalendarDays, MessageSquare, Wrench, FileText, Smartphone, ShoppingCart, ListChecks, BarChart3, DollarSign, Megaphone, Gift, Package, Building2, Receipt, Folder, ArrowLeft } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { getSiteUrl } from '../lib/siteConfig';
import { businessNeedsExistingSubscriptionBillingRecovery } from '../lib/subscription';
import {
  fetchReferralSignupOffer,
  formatReferralDiscountLabel,
  type ReferralSignupOffer,
} from '../lib/referralPartnerOffer';
import { getSignupPlanSelection } from '../lib/signupPlanSelection';
import { payOpenPlatformInvoice } from '../lib/stripeCheckout';
import { trackMetaStartTrial } from '../lib/metaPixel';
import toast from 'react-hot-toast';

interface SubscriptionPlan {
  plan_id: string;
  plan_name: string;
  plan_price: number;
  included_apps: string[];
  included_power_ups: string[];
  max_resources: number;
  /** Free trial length for first-time subscribers (0/undefined = no trial). */
  trial_days?: number;
  stripe_price_id?: string | null;
}

/** Short "who is this for" blurb per plan, shown on the selection cards. */
const PLAN_SUMMARIES: Record<string, string> = {
  core: 'Best for solo operators — everything you need to book jobs, manage customers, and get paid.',
  'core-plus-teams': 'Best for growing businesses with a crew — all of Core plus team scheduling, tracking, and timesheets.',
  growth: 'Best for established teams ready to scale — the full platform including marketing, automations, and AI.',
  'ai-max':
    'Best for AI-heavy operations — every available app, with the most AI included. No trial; billed from day one.',
};

type SubscriptionMgmtLocationState = {
  fromSignup?: boolean;
  fromFrozenAccount?: boolean;
  fromPaymentRecovery?: boolean;
  fromPastDueLock?: boolean;
  message?: string;
  userId?: string;
  email?: string;
};

const AdminSubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locState = (location.state || {}) as SubscriptionMgmtLocationState;
  const fromPastDueLockRoute = Boolean(locState.fromPastDueLock);
  const fromPaymentRecoveryRoute = Boolean(locState.fromPaymentRecovery);
  const { 
    subscription, 
    isActive, 
    isLoading, 
    error, 
    activateSubscription,
    openPlatformBillingPortal,
    refreshSubscription
  } = useSubscription();
  
  const { selectedBusiness, loading: businessLoading, forceLoadBusinesses } = useBusiness();
  const [activatingPlan, setActivatingPlan] = useState<string | null>(null);
  const [fromSignup, setFromSignup] = useState(false);
  const [fromFrozenAccount, setFromFrozenAccount] = useState(false);
  const [fromPaymentRecovery, setFromPaymentRecovery] = useState(false);
  const [signupData, setSignupData] = useState<any>(null);
  const [frozenAccountData, setFrozenAccountData] = useState<any>(null);
  const [paymentRecoveryData, setPaymentRecoveryData] = useState<any>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  /** Plan chosen during the extended signup flow — highlighted as "Your pick". */
  const signupSelectedPlanId = useMemo(() => getSignupPlanSelection()?.planId ?? null, []);
  const [plansLoading, setPlansLoading] = useState(true);
  const [referralOffer, setReferralOffer] = useState<ReferralSignupOffer | null>(null);

  // Handle signup flow data and frozen account data
  useEffect(() => {
    const {
      fromSignup: isFromSignup,
      fromFrozenAccount: isFromFrozen,
      fromPaymentRecovery: isPaymentRecovery,
      fromPastDueLock: isPastDueLock,
      userId,
      email,
      message,
    } = location.state || {};
    if (isFromSignup) {
      setFromSignup(true);
      setSignupData({ userId, email, message });
      console.log('📋 User coming from signup flow:', { userId, email });
    }
    if (isFromFrozen) {
      setFromFrozenAccount(true);
      setFrozenAccountData({ userId, email, message });
      console.log('🧊 User coming from frozen account:', { userId, email, message });
      if (message) {
        toast.error(message, { duration: 6000 });
      }
    }
    if (isPaymentRecovery) {
      setFromPaymentRecovery(true);
      setPaymentRecoveryData({ userId, email, message });
      console.log('💳 User coming for payment recovery:', { userId, email });
      if (message) {
        toast(message, { duration: 6000, icon: '💳' });
      }
    }
    if (isPastDueLock) {
      toast.error(
        message ||
          'Your subscription is more than 4 days past due. Pay the open invoice or update your card to continue.',
        { duration: 8000 },
      );
    }
  }, [location.state]);

  const needsExistingSubscriptionPay = useMemo(
    () =>
      businessNeedsExistingSubscriptionBillingRecovery(
        subscription?.subscription_status ?? selectedBusiness?.subscription_status,
        subscription?.stripe_subscription_id ?? selectedBusiness?.stripe_subscription_id,
      ),
    [
      subscription?.subscription_status,
      subscription?.stripe_subscription_id,
      selectedBusiness?.subscription_status,
      selectedBusiness?.stripe_subscription_id,
    ],
  );

  const hasPlatformStripeSub = useMemo(() => {
    const sid = subscription?.stripe_subscription_id ?? selectedBusiness?.stripe_subscription_id;
    return typeof sid === 'string' && sid.trim() !== '';
  }, [subscription?.stripe_subscription_id, selectedBusiness?.stripe_subscription_id]);

  const billingRecoveryMode = useMemo(
    () =>
      needsExistingSubscriptionPay ||
      fromPaymentRecoveryRoute ||
      (fromPastDueLockRoute && hasPlatformStripeSub),
    [
      needsExistingSubscriptionPay,
      fromPaymentRecoveryRoute,
      fromPastDueLockRoute,
      hasPlatformStripeSub,
    ],
  );

  const showPayExistingCard = useMemo(() => {
    if (needsExistingSubscriptionPay) return true;
    if (!fromPaymentRecoveryRoute && !fromPastDueLockRoute) return false;
    const sid = subscription?.stripe_subscription_id ?? selectedBusiness?.stripe_subscription_id;
    return typeof sid === 'string' && sid.trim() !== '';
  }, [
    needsExistingSubscriptionPay,
    fromPaymentRecoveryRoute,
    fromPastDueLockRoute,
    subscription?.stripe_subscription_id,
    selectedBusiness?.stripe_subscription_id,
  ]);

  const handleOpenBillingPortal = async () => {
    const returnUrl = `${getSiteUrl()}/admin-subscription-management`;
    const ok = await openPlatformBillingPortal(returnUrl);
    if (!ok) {
      toast.error('Could not open billing. Check the error message above or contact support.');
    }
  };

  const [payingInvoice, setPayingInvoice] = useState(false);
  /** Hosted Stripe invoice page — shown when the card on file was declined again. */
  const [declinedInvoiceUrl, setDeclinedInvoiceUrl] = useState<string | null>(null);

  const handlePayOpenInvoice = async () => {
    if (!selectedBusiness?.id || payingInvoice) return;
    setPayingInvoice(true);
    setDeclinedInvoiceUrl(null);
    try {
      const result = await payOpenPlatformInvoice(selectedBusiness.id);
      if (result.paid) {
        toast.success('Payment successful — your subscription is restored.');
        await refreshSubscription();
        return;
      }
      if (result.noOpenInvoice) {
        toast('No open invoice found — your billing may already be settled. Refreshing…', {
          icon: 'ℹ️',
        });
        await refreshSubscription();
        return;
      }
      setDeclinedInvoiceUrl(result.hostedInvoiceUrl ?? null);
      toast.error(
        result.message
          ? `Card was declined: ${result.message}`
          : 'Your card was declined again. Update your payment method and retry.',
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Payment attempt failed. Please try again.');
    } finally {
      setPayingInvoice(false);
    }
  };

  // Safety net: if coming from signup and business isn't loaded yet, retry loading
  useEffect(() => {
    if (fromSignup && !selectedBusiness && !businessLoading) {
      console.log('🔄 Business not loaded after signup, retrying...');
      forceLoadBusinesses();
    }
  }, [fromSignup, selectedBusiness, businessLoading]);

  useEffect(() => {
    if ((fromPaymentRecoveryRoute || fromPastDueLockRoute) && !selectedBusiness && !businessLoading) {
      forceLoadBusinesses();
    }
  }, [fromPaymentRecoveryRoute, fromPastDueLockRoute, selectedBusiness, businessLoading, forceLoadBusinesses]);

  useEffect(() => {
    let cancelled = false;

    const loadReferralOffer = async () => {
      // Only trust the business row (immutable first-touch attribution set at
      // signup). Never fall back to the browser-persisted ?ref= code: existing
      // customers who merely visited a partner link would otherwise see
      // "Referred by partner" on their billing page despite never being
      // attributed (no commission or discount actually applies to them).
      const code =
        (selectedBusiness as { referral_partner_code?: string | null } | null)
          ?.referral_partner_code || null;
      if (!code) {
        setReferralOffer(null);
        return;
      }

      try {
        const offer = await fetchReferralSignupOffer(code);
        if (!cancelled) setReferralOffer(offer.ok ? offer : null);
      } catch {
        if (!cancelled) setReferralOffer(null);
      }
    };

    void loadReferralOffer();
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, selectedBusiness]);

  // Fetch subscription plans from database
  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      setPlansLoading(true);
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('plan_price', { ascending: true });

      if (error) {
        console.error('Error fetching subscription plans:', error);
        toast.error('Failed to load subscription plans');
        return;
      }

      // Hosting rows (kind 'hosting') are billed separately — not platform plans.
      // Plans without a Stripe price yet aren't sellable, so they're hidden too.
      setPlans(
        (data || []).filter(
          (p) => p.kind !== 'hosting' && p.plan_id !== 'nexus-hosting' && !!p.stripe_price_id,
        ),
      );
      console.log('📊 Loaded subscription plans:', data);
    } catch (err) {
      console.error('Error in fetchSubscriptionPlans:', err);
      toast.error('Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleActivatePlan = async (planId: string) => {
    setActivatingPlan(planId);
    try {
      const success = await activateSubscription(planId);
      if (!success) {
        toast.error(error || 'Unable to activate plan. Please check the message above or try again.');
        return;
      }
      {
        const selectedPlan = plans.find(plan => plan.plan_id === planId);
        console.log(`🎉 Successfully activated ${selectedPlan?.plan_name} subscription!`);

        // Meta conversion: fire StartTrial only after a real trial activation succeeds.
        if ((selectedPlan?.trial_days ?? 0) > 0) {
          trackMetaStartTrial({ planId });
        }

        // After activating subscription, check if user has completed setup
        if (!supabase) {
          console.error('Supabase not available');
          return;
        }

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('No user found');
          return;
        }

        // Check if setup is completed
        const { data: setupStatus } = await supabase
          .from('user_setup_status')
          .select('completed')
          .eq('user_id', user.id)
          .single();

        if (!setupStatus || !setupStatus.completed) {
          // User hasn't completed setup, redirect to setup flow
          console.log('🔄 Redirecting to setup flow - setup not completed');
          navigate('/setup', { 
            state: { 
              userId: user.id,
              email: user.email,
              message: 'Payment successful! Let\'s complete your setup.' 
            }
          });
        } else {
          // Setup is completed, redirect to main app
          console.log('✅ Setup already completed, redirecting to main app');
          window.location.href = '/';
        }
      }
    } finally {
      setActivatingPlan(null);
    }
  };

  const getStatusIcon = () => {
    if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-gray-400" />;
    if (isActive) return <CheckCircle className="h-5 w-5 text-green-500" />;
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = () => {
    if (isLoading) return 'Loading...';
    if (isActive) return 'Active';
    return 'Inactive';
  };

  const getStatusColor = () => {
    if (isLoading) return 'text-gray-500';
    if (isActive) return 'text-green-600';
    return 'text-red-600';
  };

  const customAppIcons: { [key: string]: string } = {
    'analytics': '/Analyticsappicon.png',
    'projects': '/Projectsappicon.png',
    'calendar': '/Calenderappicon.png',
    'crm': '/CRMappicon.png',
    'proposals': '/Proposalsappicon.png',
    'tasks': '/Bookingsappicon.png',
    'inventory': '/Inventoryappicon.png',
    'expenses': '/Expensesappicon.png',
    'payroll': '/Timesheetsappicon.png',
    'teams': '/Teamsappicon.png',
    'invoices': '/Invoicesappicon.png',
    'automations': '/Automateappicon.png',
    'services': '/Servicesappicon.png',
    'customers': '/Customersappicon.png',
    'marketing': '/Marketingappicon.png',
    'gift-cards': '/Giftcardsappicon.png',
    'messages': '/Messagesappicon.png',
    'bookings': '/Bookingsappicon.png',
    'products': '/Storeappicon.png',
    'mobile-calendar': '/Fieldproappicon.png',
    'm-calendar': '/Fieldproappicon.png',
  };

  const getAppIconBySlug = (appSlug: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'calendar': <Calendar className="h-5 w-5" />,
      'mobile-calendar': <Smartphone className="h-5 w-5" />,
      'm-calendar': <Smartphone className="h-5 w-5" />,
      'customers': <Users className="h-5 w-5" />,
      'automations': <Zap className="h-5 w-5" />,
      'teams': <Users className="h-5 w-5" />,
      'tasks': <ListChecks className="h-5 w-5" />,
      'analytics': <BarChart3 className="h-5 w-5" />,
      'crm': <Building2 className="h-5 w-5" />,
      'inventory': <Package className="h-5 w-5" />,
      'payroll': <DollarSign className="h-5 w-5" />,
      'services': <Wrench className="h-5 w-5" />,
      'bookings': <CalendarDays className="h-5 w-5" />,
      'proposals': <FileText className="h-5 w-5" />,
      'marketing': <Megaphone className="h-5 w-5" />,
      'invoices': <Receipt className="h-5 w-5" />,
      'messages': <MessageSquare className="h-5 w-5" />,
      'products': <ShoppingCart className="h-5 w-5" />,
      'gift-cards': <Gift className="h-5 w-5" />,
      'projects': <Folder className="h-5 w-5" />,
    };
    return iconMap[appSlug] || <Package className="h-5 w-5" />;
  };

  const getAppIconBySlugSmall = (appSlug: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'calendar': <Calendar className="h-3 w-3" />,
      'mobile-calendar': <Smartphone className="h-3 w-3" />,
      'm-calendar': <Smartphone className="h-3 w-3" />,
      'customers': <Users className="h-3 w-3" />,
      'automations': <Zap className="h-3 w-3" />,
      'teams': <Users className="h-3 w-3" />,
      'tasks': <ListChecks className="h-3 w-3" />,
      'analytics': <BarChart3 className="h-3 w-3" />,
      'crm': <Building2 className="h-3 w-3" />,
      'inventory': <Package className="h-3 w-3" />,
      'payroll': <DollarSign className="h-3 w-3" />,
      'services': <Wrench className="h-3 w-3" />,
      'bookings': <CalendarDays className="h-3 w-3" />,
      'proposals': <FileText className="h-3 w-3" />,
      'marketing': <Megaphone className="h-3 w-3" />,
      'invoices': <Receipt className="h-3 w-3" />,
      'messages': <MessageSquare className="h-3 w-3" />,
      'products': <ShoppingCart className="h-3 w-3" />,
      'gift-cards': <Gift className="h-3 w-3" />,
      'projects': <Folder className="h-3 w-3" />,
    };
    return iconMap[appSlug] || <Package className="h-3 w-3" />;
  };

  const renderAppIconTile = (appId: string, size: string = "w-5 h-5") => {
    const appSlug = appId.replace('-addon-app', '').replace('-core-app', '');
    if (customAppIcons[appSlug]) {
      return (
        <div className={`${size} rounded-md overflow-hidden bg-white shadow-sm mr-2 flex-shrink-0`}>
          <img src={customAppIcons[appSlug]} alt={appSlug} className="w-full h-full object-cover" draggable={false} style={{ imageRendering: '-webkit-optimize-contrast' as any }} />
        </div>
      );
    }
    return (
      <div className={`${size} bg-gradient-to-br from-blue-600 to-blue-500 rounded-md flex items-center justify-center text-white shadow-sm mr-2 flex-shrink-0`}>
        {getAppIconBySlugSmall(appSlug)}
      </div>
    );
  };

  const getAppIcon = (appId: string) => {
    const appSlug = appId
      .replace('-addon-app', '')
      .replace('-core-app', '');
    return getAppIconBySlug(appSlug);
  };

  const getAppIconSmall = (appId: string) => {
    const appSlug = appId
      .replace('-addon-app', '')
      .replace('-core-app', '');
    return getAppIconBySlugSmall(appSlug);
  };

  // Helper function to display app names (matching MyApps)
  const getAppName = (appId: string) => {
    const nameMap: { [key: string]: string } = {
      'bookings-core-app': 'Bookings',
      'calendar-core-app': 'Calendar',
      'customers-core-app': 'Customers',
      'services-core-app': 'Services',
      'messages-core-app': 'Messages',
      'invoices-core-app': 'Invoices',
      'automations-core-app': 'Automate',
      'mobile-calendar-core-app': 'Field-Pro',
      'products-addon-app': 'Store',
      'payroll-addon-app': 'T-Sheets',
    };
    return nameMap[appId] || appId.replace('-core-app', '').replace('-addon-app', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div
      className="min-h-screen bg-white text-gray-900 relative overflow-x-hidden"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-100 relative z-10">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Back to Login - signs out then full-page go to login so session is cleared */}
          <div className="mb-6">
            <button
              onClick={async () => {
                await supabase?.auth.signOut();
                window.location.href = '/login';
              }}
              className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </button>
          </div>
          
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 mb-2">
              {fromSignup ? (
                <>Complete Your <span className="text-emerald-600">Setup</span></>
              ) : billingRecoveryMode ? (
                'Pay your existing subscription'
              ) : (
                'Subscription Management'
              )}
            </h1>
            <p className="text-gray-600">
              {fromSignup
                ? 'Select your subscription plan to continue'
                : billingRecoveryMode
                  ? 'You already have a Nexus plan in Stripe. Update your card or pay the open invoice to restore your account — plan changes are not available while payment is past due.'
                  : 'Manage your subscription and billing'}
            </p>
            {fromSignup && signupData?.message && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl inline-block">
                <p className="text-emerald-700 text-sm">{signupData.message}</p>
              </div>
            )}
            {referralOffer?.signup_discount_enabled && referralOffer.signup_discount_percent ? (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl inline-block max-w-lg mx-auto">
                <p className="text-emerald-700 text-sm font-semibold">
                  Referred by {referralOffer.partner_name}
                </p>
                <p className="text-emerald-600 text-sm mt-1">
                  {formatReferralDiscountLabel(referralOffer.signup_discount_percent)} — applied at
                  checkout automatically.
                </p>
              </div>
            ) : null}
            {fromFrozenAccount && frozenAccountData?.message && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl inline-block">
                <p className="text-red-700 text-sm font-medium">{frozenAccountData.message}</p>
                <p className="text-red-500 text-xs mt-1">Please update your payment method and reactivate your subscription to continue.</p>
              </div>
            )}
            {fromPaymentRecovery && paymentRecoveryData?.message && !fromFrozenAccount && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl inline-block max-w-xl mx-auto">
                <p className="text-amber-700 text-sm font-medium">{paymentRecoveryData.message}</p>
                <p className="text-amber-600 text-xs mt-2">
                  Use the button below to open Stripe billing for your current subscription.
                </p>
              </div>
            )}
            {fromPastDueLockRoute &&
              !fromPaymentRecoveryRoute &&
              !fromFrozenAccount &&
              hasPlatformStripeSub && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl inline-block max-w-xl mx-auto">
                <p className="text-amber-700 text-sm font-medium">
                  Your account is limited to billing until the subscription is no longer severely past due.
                </p>
                <p className="text-amber-600 text-xs mt-2">
                  After 4 days in past due, access is restricted here until payment succeeds. If Stripe cancels the
                  subscription entirely, you will need to pick a plan again below.
                </p>
              </div>
            )}
            {fromPastDueLockRoute &&
              !fromPaymentRecoveryRoute &&
              !fromFrozenAccount &&
              !hasPlatformStripeSub && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl inline-block max-w-xl mx-auto">
                <p className="text-gray-700 text-sm font-medium">
                  This subscription is no longer active in Stripe. Choose a plan below to subscribe again.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 relative z-10">

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-3" />
              <span className="text-red-700 font-medium">Error: {error}</span>
            </div>
          </div>
        )}

        {showPayExistingCard && (
          <div className="mb-8 p-6 bg-amber-50/60 rounded-2xl border border-amber-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <CreditCard className="h-8 w-8 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Update billing on your current subscription</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Your subscription payment is past due. Retry the charge on your card right away, or update your
                    payment method first — this keeps your existing plan and renewal date.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:items-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handlePayOpenInvoice}
                  disabled={payingInvoice || isLoading}
                  className="inline-flex items-center justify-center px-5 py-3 rounded-full bg-gray-900 text-white font-semibold hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {payingInvoice ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Charging card…
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay now with card on file
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleOpenBillingPortal}
                  disabled={isLoading || payingInvoice}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-gray-300 bg-white text-gray-800 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Opening…
                    </>
                  ) : (
                    'Update payment method'
                  )}
                </button>
              </div>
            </div>
            {declinedInvoiceUrl && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-red-700">
                  Your card was declined again. You can pay this invoice directly with a different card on Stripe's
                  secure invoice page.
                </p>
                <a
                  href={declinedInvoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center justify-center px-4 py-2 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
                >
                  Pay invoice with another card
                </a>
              </div>
            )}
          </div>
        )}

        {/* Current Plan Status */}
        {subscription && subscription.active_plan_id && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Current Plan</h2>
                <p className="text-gray-600">
                  {plans.find(p => p.plan_id === subscription.active_plan_id)?.plan_name || 
                   subscription.active_plan_id.charAt(0).toUpperCase() + subscription.active_plan_id.slice(1) + ' Plan'}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {plans.find(p => p.plan_id === subscription.active_plan_id)?.plan_price ? 
                    `$${plans.find(p => p.plan_id === subscription.active_plan_id)?.plan_price}/mo` : 
                    'N/A'
                  }
                </div>
                <p
                  className={`text-sm ${
                    subscription.subscription_status === 'active' || subscription.subscription_status === 'trialing'
                      ? 'text-emerald-600'
                      : subscription.subscription_status === 'past_due'
                        ? 'text-amber-600'
                        : 'text-red-600'
                  }`}
                >
                  Status: {subscription.subscription_status || 'unknown'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tier selection hidden while past_due / payment recovery on existing Stripe sub */}
        {!showPayExistingCard && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Your Plan</h2>
          
          {plansLoading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading plans...</p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 ${plans.length >= 4 ? 'xl:grid-cols-4' : 'lg:grid-cols-3'}`}>
              {plans.map((plan) => (
                <div
                  key={plan.plan_id}
                  className={`bg-white rounded-2xl border-2 p-6 transition-all duration-200 flex flex-col ${
                    signupSelectedPlanId === plan.plan_id
                      ? 'border-gray-900 shadow-[0_4px_20px_rgba(0,0,0,0.08)]'
                      : 'border-gray-200 hover:border-gray-300 shadow-[0_1px_3px_rgba(0,0,0,0.05)]'
                  }`}
                >
                  {/* Plan Header */}
                  <div className="text-center mb-4">
                    {signupSelectedPlanId === plan.plan_id && (
                      <span className="inline-block mb-2 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-gray-900 text-white border border-gray-900">
                        Your pick
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.plan_name}</h3>
                    {(plan.trial_days ?? 0) > 0 && (
                      <span className="inline-block mb-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {plan.trial_days}-day free trial · no card required
                      </span>
                    )}
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      ${plan.plan_price}
                    </div>
                    <p className="text-sm text-gray-500">per month</p>
                    <p className="mt-1.5 text-xs font-medium text-blue-700">AI included</p>
                  </div>

                  {/* Who it's for */}
                  {PLAN_SUMMARIES[plan.plan_id] && (
                    <p className="text-[13px] text-gray-600 leading-relaxed text-center mb-4 min-h-[58px]">
                      {PLAN_SUMMARIES[plan.plan_id]}
                    </p>
                  )}

                  {/* Team Members */}
                  <div className="mb-4 text-center">
                    <p className="text-sm text-gray-500">
                      {plan.max_resources > 0 ? `${plan.max_resources} team members` : 'Unlimited team members'}
                    </p>
                  </div>

                  {/* Included Features */}
                  <div className="mb-6 flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Included Features</h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {plan.included_apps
                        // Field-Pro is retired — hidden even if still present in plan data.
                        .filter((appId) => !appId.startsWith('mobile-calendar') && !appId.startsWith('m-calendar'))
                        .map((appId) => (
                        <div key={appId} className="flex items-center text-xs text-gray-700">
                          {renderAppIconTile(appId)}
                          {getAppName(appId)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Activate Button */}
                  <button
                    onClick={() => handleActivatePlan(plan.plan_id)}
                    disabled={isLoading || activatingPlan === plan.plan_id}
                    className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-gray-900 hover:bg-black rounded-full transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {activatingPlan === plan.plan_id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Activating...
                      </>
                    ) : (plan.trial_days ?? 0) > 0 ? (
                      `Start ${plan.trial_days}-day free trial`
                    ) : (
                      'Activate Plan'
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

      </div>
    </div>
  );
};

export default AdminSubscriptionManagement; 